import {
  createBrowserRouter,
  Outlet,
  useLoaderData,
  Link,
  createRoutesFromElements,
  Route,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  DashboardPage,
  StampsPage,
  RewardsPage,
  AwardsPage,
  Page401,
  Page404,
  ExtrasPage,
  FaqsPage,
  MovementsPage,
  TermsConditionsPage,
  PrivacyPolicyPage,
  OnboardingPage,
  StatePicker,
  StarsPage,
  StarsDetailPage,
} from "./pages";
import {
  tokenSolver,
  headerKeysAllowed,
  privTermsAccepted,
  privSponsorKey,
  privTokenKey,
  privIsValidToken,
  privStatePicked,
  privGtmKey,
  scrollToBottom,
  appVersion,
  states,
} from "./utils";
import { authUserOxxo, authUserSpin } from "./api";
import { getImg, gtmId } from "./utils";
import { boton_spin, logo_color, up, down } from "./pngs";
import useCollapse from "react-collapsed";
import TagManager from "react-gtm-module";
import { useEffect, useState } from "react";
import { CoalitionSpinner, isTokenValid } from "./components";
import {
  sendTag,
  event_properties_105_01,
  event_properties_105_02,
  event_properties_105_03,
} from "./components/CoalitionGtm";

const authLoader = async (navigate) => {
  const params = new URLSearchParams(window.location.search);
  const allowedValues = Object.values(headerKeysAllowed);
  let received = [];
  for (const key of params.keys()) {
    const value = params.get(key);
    if (allowedValues.includes(key) && value && value.length > 1) {
      received.push(key);
    }
  }
  if (received.length !== 1) return navigate("/unauth");
  const headerKey = received[0];
  const headerValue = params.get(headerKey);
  localStorage.setItem(privSponsorKey, headerKey);
  localStorage.setItem(privTokenKey, headerValue);
  const pattern = /\/stars\/[0-9]+$/;
  if (pattern.test(window.location.pathname)) {
    const oldCity = localStorage.getItem(privStatePicked);
    const oldTerms = localStorage.getItem(privTermsAccepted);
    if (!oldCity || !oldTerms) {
      const city = '{"value":"CDMX","label":"Ciudad de México"}';
      localStorage.setItem(privStatePicked, city);
      localStorage.setItem(privTermsAccepted, true);
    }
  }
  if (headerKey === headerKeysAllowed.oxxo) {
    await authUserOxxo(headerValue);
  } else if (headerKey === headerKeysAllowed.spin) {
    await authUserSpin(headerValue);
    localStorage.setItem(privTermsAccepted, true);
  }
  if (!isTokenValid()) return navigate("/unauth");
  return true;
};

const Footer = () => {
  const gtm = localStorage.getItem(privGtmKey);
  const sendEvent = (payload) => {
    sendTag(gtm, payload);
    getToggleProps();
  };

  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
  const keyProvided = localStorage.getItem(privSponsorKey);

  useEffect(() => {
    if (isExpanded) setTimeout(() => scrollToBottom(), 200);
  }, [isExpanded]);

  return (
    <div className="app-footer">
      {keyProvided === headerKeysAllowed.spin &&
      window.location.pathname === "/" ? (
          <div
            className="go-spin-button-container"
            onClick={async () => {
              await window.ReactNativeWebView.postMessage("return:home");
            }}
            onKeyPress={async () => {
              await window.ReactNativeWebView.postMessage("return:home");
            }}
            role="button"
            tabIndex={0}
          >
            {getImg(boton_spin, `go-spin-button${isExpanded ? " added" : ""}`)}
          </div>
        ) : null}
      <div className="footer-logo">
        <Link to={`/?${tokenSolver()}`}>{getImg(logo_color)}</Link>
      </div>
      <div className="menu-content" {...getCollapseProps()}>
        <div className="menu-item" {...getToggleProps()}>
          <Link
            to={`/reset?${tokenSolver()}`}
            onClick={() => localStorage.removeItem(privStatePicked)}
          >
            Cambiar ubicación
          </Link>
        </div>
        <div className="menu-item" {...getToggleProps()}>
          <Link
            to={`/faqs?${tokenSolver()}`}
            onClick={() => sendEvent(event_properties_105_01())}
          >
            Preguntas frecuentes
          </Link>
        </div>
        <div className="menu-item" {...getToggleProps()}>
          <Link
            to={`/terminos-y-condiciones-del-programa-auth?${tokenSolver()}`}
            onClick={() => sendEvent(event_properties_105_02())}
          >
            Términos y condiciones
          </Link>
        </div>
        <div className="menu-item" {...getToggleProps()}>
          <Link
            to={`/aviso-de-privacidad-auth?${tokenSolver()}`}
            onClick={() => sendEvent(event_properties_105_03())}
          >
            Aviso de privacidad
          </Link>
        </div>
      </div>
      <div className="rigths-reserved">
        2023 Todos los derechos reservados ©{" "}
        {isExpanded ? <>{appVersion()}</> : null}
      </div>
      <div
        className={`collapsible-${isExpanded ? "open" : "closed"}`}
        {...getToggleProps()}
      >
        <div className="menu-trigger">{getImg(isExpanded ? up : down)}</div>
      </div>
    </div>
  );
};

const RootElement = () => {
  useLoaderData();
  // Initialize GTM
  if (gtmId && gtmId.startsWith("GTM-")) {
    TagManager.initialize({ gtmId });
    localStorage.setItem(privGtmKey, gtmId);
  }

  // Authentication
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchAuth = async () => {
      const auth = await authLoader(navigate);
      setLoading(false);
      if (!auth) return navigate("/unauth");
    };
    fetchAuth();
  }, [loading, navigate]);

  // Token valid, State selected, Terms accepted, Stars required
  const { pathname } = window.location;
  const [verifyState, setVerifyState] = useState(false);
  const [isValidToken, setIsValidToken] = useState();
  const [statePicked, setStatePicked] = useState();
  const [termsAccepted, setTermsAccepted] = useState();
  const [canShowContent, setCanShowContent] = useState(true);
  const swapVerifyState = () => setVerifyState(true);
  useEffect(() => {
    const showContent = async () => {
      setCanShowContent(true);
      setIsValidToken(localStorage.getItem(privIsValidToken));
      const prevState = localStorage.getItem(privStatePicked);
      let stateToStore = null;
      const found = states.find((state) => JSON.stringify(state) === prevState);
      if (found) stateToStore = prevState;
      setStatePicked(stateToStore);
      setTermsAccepted(localStorage.getItem(privTermsAccepted));
      if (
        !isValidToken ||
        !statePicked ||
        statePicked === "undefined" ||
        !termsAccepted ||
        pathname.startsWith("/stars")
      ) {
        setCanShowContent(false);
      }
      if (verifyState) setVerifyState(false);
    };
    showContent();
  }, [
    verifyState,
    loading,
    isValidToken,
    statePicked,
    termsAccepted,
    canShowContent,
    pathname,
  ]);

  return (
    <>
      {loading ? (
        <CoalitionSpinner />
      ) : (
        <>
          {isValidToken ? (
            <>
              {!statePicked ? (
                <StatePicker registerState={() => swapVerifyState()} />
              ) : (
                <>
                  {!termsAccepted ? (
                    <OnboardingPage registerTerms={() => swapVerifyState()} />
                  ) : (
                    <>
                      {<Outlet />}
                      {canShowContent ? <Footer /> : null}
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Page401 ctx={"Verifying Token"} />
            </>
          )}
        </>
      )}
    </>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/" element={<RootElement />}>
        <Route index element={<DashboardPage />} />
        <Route path="reset" element={<Navigate to="/" replace />} />
        <Route path="stamps" element={<StampsPage />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="awards" element={<AwardsPage />} />
        <Route path="extras" element={<ExtrasPage />} />
        <Route path="stars" element={<StarsPage />} />
        <Route path="stars/:promoId" element={<StarsDetailPage />} />
        <Route path="faqs" element={<FaqsPage />} />
        <Route path="movements" element={<MovementsPage />} />
        <Route
          path="terminos-y-condiciones-del-programa-auth"
          element={<TermsConditionsPage />}
        />
        <Route
          path="aviso-de-privacidad-auth"
          element={<PrivacyPolicyPage />}
        />
        <Route path="*" element={<Page404 />} />
      </Route>
      <Route
        path="terminos-y-condiciones-del-programa"
        element={<TermsConditionsPage fh={true} />}
      />
      <Route
        path="aviso-de-privacidad"
        element={<PrivacyPolicyPage fh={true} />}
      />
      <Route path="unauth" element={<Page401 ctx={"Redirected"} />} />
    </>
  )
);
